import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, bacaToken, simpanToken, hapusToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(true);
    const navigate = useNavigate();

    const refresh = useCallback(async () => {
        const token = bacaToken();
        if (!token) {
            setUser(null);
            setChecking(false);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            hapusToken();
            setUser(null);
            return null;
        } finally {
            setChecking(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Dipicu interceptor 401 global (lib/api.js) dari endpoint MANAPUN kecuali alur
    // login/verifikasi-OTP (401 di situ berarti kredensial salah, bukan sesi mati).
    // Sumbernya bisa macam-macam: token expired, token dicabut (F-01), atau akun
    // dinonaktifkan admin (F-02) -- semuanya berujung sama: paksa keluar + redirect.
    useEffect(() => {
        const handleSessionExpired = () => {
            hapusToken();
            setUser(null);
            navigate("/login", { replace: true, state: { sessionExpired: true } });
        };
        window.addEventListener("auth:session-expired", handleSessionExpired);
        return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
    }, [navigate]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.otp_required) {
                // Role dgn 2FA EMAIL lama (lihat REQUIRE_2FA_ROLES di server.py -- default
                // kosong, jalur ini nonaktif kecuali diaktifkan lagi lewat env): password
                // benar, tapi sesi belum aktif -- belum ada access_token.
                return { ok: true, otpRequired: true, otpToken: data.otp_token };
            }
            if (data.totp_required) {
                // super_admin, TOTP sudah terdaftar -- lanjut ke /verifikasi-totp.
                return { ok: true, totpRequired: true, challengeToken: data.challenge_token };
            }
            if (data.totp_enroll_required) {
                // super_admin, TOTP WAJIB tapi belum terdaftar -- lanjut ke pendaftaran.
                return { ok: true, totpEnrollRequired: true, enrollToken: data.enroll_token };
            }
            simpanToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const verifyOtp = async (otpToken, code) => {
        try {
            const { data } = await api.post("/auth/verify-otp", { otp_token: otpToken, code });
            simpanToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const resendOtp = async (otpToken) => {
        try {
            const { data } = await api.post("/auth/resend-otp", { otp_token: otpToken });
            return { ok: true, otpToken: data.otp_token };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    // TOTP authenticator app (super_admin) -- lihat juga verifyOtp/resendOtp di atas
    // (jalur EMAIL lama, terpisah). code DAN recoveryCode saling eksklusif: isi salah
    // satu saja (backend menolak kalau dua2nya kosong).
    const verifyTotp = async (challengeToken, { code, recoveryCode } = {}) => {
        try {
            const { data } = await api.post("/auth/verify-totp", {
                challenge_token: challengeToken,
                code: code || undefined,
                recovery_code: recoveryCode || undefined,
            });
            simpanToken(data.access_token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    // Pendaftaran TOTP pertama kali (dipaksa saat login -- lihat totp_enroll_required
    // di atas). start = ambil secret+QR SEKALI; confirm = verifikasi kode pertama,
    // balikannya SATU-SATUNYA saat kode pemulihan mentah muncul.
    const totpEnrollStart = async (enrollToken) => {
        try {
            const { data } = await api.post("/auth/totp/enroll/start", { enroll_token: enrollToken });
            return { ok: true, ...data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const totpEnrollConfirm = async (enrollToken, code) => {
        try {
            const { data } = await api.post("/auth/totp/enroll/confirm", { enroll_token: enrollToken, code });
            simpanToken(data.access_token);
            setUser(data);
            return { ok: true, user: data, recoveryCodes: data.recovery_codes };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        hapusToken();
        setUser(null);
    };

    // Bersihkan sesi LOKAL saja, TANPA panggil /auth/logout -- dipakai halaman
    // reset-password saat dibuka: kalau browser kebetulan sedang login (mis. link
    // dibuka di komputer bersama), keluarkan tampilan itu dulu supaya tidak
    // tertukar akun. Sengaja tidak lewat endpoint /auth/logout: kalau tidak ada
    // sesi sama sekali, panggilan itu akan gagal 401 dan memicu interceptor 401
    // global (lib/api.js) yang malah memaksa redirect ke /login.
    const clearSession = () => {
        hapusToken();
        setUser(null);
    };

    const changePassword = async (current_password, new_password) => {
        try {
            const { data } = await api.post("/auth/change-password", { current_password, new_password });
            // Backend mencabut SEMUA token lama (termasuk token sesi ini) begitu password
            // diganti, lalu mengirim token baru -- wajib disimpan SEBELUM refresh(), kalau
            // tidak /auth/me memakai token lama -> 401 -> user terlempar ke /login.
            if (data?.access_token) simpanToken(data.access_token);
            await refresh();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const resetPasswordConfirm = async (token, new_password) => {
        try {
            await api.post("/auth/reset-password/confirm", { token, new_password });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    // Cek validitas token SAAT HALAMAN reset-password DIBUKA (bukan cuma saat
    // submit) -- read-only, tidak menghabiskan token. Gagal jaringan diperlakukan
    // sbg "tidak valid" supaya halaman tidak menampilkan form password kalau
    // status link belum bisa dipastikan.
    const validateResetToken = async (token) => {
        try {
            // POST + token di body (bukan ?token=) supaya token tidak tercatat di log akses server.
            const { data } = await api.post("/auth/reset-password/validate", { token });
            return data.valid === true;
        } catch {
            return false;
        }
    };

    return (
        <AuthContext.Provider value={{
            user, checking, login, verifyOtp, resendOtp, logout, clearSession, changePassword,
            resetPasswordConfirm, validateResetToken, refresh,
            verifyTotp, totpEnrollStart, totpEnrollConfirm,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
